import { useState } from "react";
import { Image, Text, View } from "react-native";

const ALPHANUMERIC = /[\p{L}\p{N}]/u;

/** Returns the shop's initials for the monogram fallback: the first letter of the first two words
 *  that carry letters or digits, else the first two letters of a single word. "WOObul Collectibles"
 *  → "WC", "Gremlin" → "GR", "Boosters & Brews" → "BB". Counted by code point, so an emoji or
 *  decorative symbol is never split into half a surrogate pair. A missing name yields "?" rather
 *  than throwing, because the badge is decoration and must never break the storefront. */
export function shopInitials(name: string | null | undefined): string {
  const trimmed = name?.trim() ?? "";
  if (!trimmed) return "?";
  const words = trimmed
    .split(/\s+/)
    .map((word) => Array.from(word).filter((char) => ALPHANUMERIC.test(char)))
    .filter((letters) => letters.length > 0);
  if (words.length === 0) return Array.from(trimmed)[0].toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).join("").toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * The vendor's shop logo: the uploaded image, or a monogram of the shop's initials on an
 * accent-tinted squircle when none is uploaded or the image fails to load.
 *
 * Shown wherever the shop's own identity leads rather than a generic name string. The tile is
 * `accent-soft`, not `surface`, so it stays a visible badge on app surfaces.
 *
 * A nullish `name` means the shop has not loaded yet and renders the bare tile to hold the slot,
 * unlike a loaded shop with a blank name, which gets `shopInitials`' "?".
 *
 * Decorative: every placement sits beside a visible name, so the badge uses `aria-hidden` rather
 * than announcing the name twice, per the cross-platform rule in `mobile/AGENTS.md` ("Design / UI").
 */
export function ShopLogo(
  { name, uri, size = 44, allowFontScaling, fallbackColor, onLoadEnd }: {
    /** The shop's name, for the monogram. Nullish means the shop is still loading. */
    name: string | null | undefined;
    /** The uploaded logo's URL; absent or failing to load shows the monogram. */
    uri?: string | null;
    size?: number;
    /** Pass `false` where the monogram must not reflow with the text-size setting. */
    allowFontScaling?: boolean;
    /** Overrides the monogram's color, for a fixed brand canvas. */
    fallbackColor?: string;
    /** Fires when the logo image finishes loading, whether or not it succeeded. */
    onLoadEnd?: () => void;
  },
) {
  const [brokenUri, setBrokenUri] = useState<string | null>(null);
  const monogram = name == null ? null : shopInitials(name);
  return (
    <View
      aria-hidden
      className="items-center justify-center overflow-hidden rounded-xl border border-border bg-accent-soft"
      style={{ width: size, height: size }}
    >
      {uri && uri !== brokenUri
        ? (
          <Image
            source={{ uri }}
            style={{ width: size, height: size }}
            resizeMode="cover"
            onError={() => setBrokenUri(uri)}
            onLoadEnd={onLoadEnd}
          />
        )
        : monogram
        ? (
          <Text
            allowFontScaling={allowFontScaling}
            className="font-title text-primary"
            style={{
              fontSize: Math.round(size * 0.42),
              ...(fallbackColor ? { color: fallbackColor } : {}),
            }}
          >
            {monogram}
          </Text>
        )
        : null}
    </View>
  );
}
